import React, { useState, ReactNode } from 'react';
import CustomDialog from 'components/CustomDialog/CustomDialog';
import { translate } from 'components/Translation';
import Button from 'components/CustomButtons/Button';
import { SingleRequest } from 'utils/api/models/requests/singleRequest';
import { RequestsService } from 'services/requests/requests';
import { Status } from './ServiceRequestDialog';
import { InfoOutlined, CheckCircleOutlined, HighlightOffOutlined } from '@material-ui/icons';
import { HeadingLBold, BodyM } from 'components/Typography/Typography';
import { primaryColor, successColor, dangerColor } from 'assets/jss/material-dashboard-pro-react';
import CustomCircularProgress from 'components/CustomCircularProgress/CustomCircularProgress';

interface IActivityDetailsProps {
	userRequest: SingleRequest | null;
	isOpen: boolean;
	turnOffDialog: () => void;
	refetchUserRequests: () => void;
}

enum RequestState {
	SHOW,
	LOADING,
	SUCCESS,
	FAILURE,
}

const AcceptRequestDialog = ({ userRequest, isOpen, turnOffDialog, refetchUserRequests }: IActivityDetailsProps) => {
	const [requestStateDialog, setRequestStateDialog] = useState(RequestState.SHOW);

	const updateUserRequest = (status: Status) => {
		if (userRequest) {
			setRequestStateDialog(RequestState.LOADING);
			RequestsService.updateUserRequest({
				userReqId: userRequest?.userReqId,
				status: status,
			}).subscribe(
				({ response }) => {
					if (response?.status === true) {
						setRequestStateDialog(RequestState.SUCCESS);
					} else {
						setRequestStateDialog(RequestState.FAILURE);
					}
				},
				() => {
					setRequestStateDialog(RequestState.FAILURE);
				}
			);
		}
	};

	const handleAccept = () => {
		updateUserRequest(Status.ACCEPTED);
	};

	const clearAndTurnOffDialog = () => {
		setRequestStateDialog(RequestState.SHOW);
		refetchUserRequests();
		turnOffDialog();
	};

	const showBody = () => {
		return userRequest?.userReqType === 'TARIFF_CHANGE'
			? [
					<BodyM>
						{translate(
							'service.requests.accept.subject',
							userRequest?.requestorMsisdn || '',
							userRequest?.reqParam.productsToAdd[0]?.name || '',
							userRequest?.reqParam.productsToAdd[1]?.name || '',
							userRequest?.amount || ''
						)}
					</BodyM>,
			  ]
			: [<BodyM>{translate('service.requests.accept.sim.body', userRequest?.requestorMsisdn || '')}</BodyM>];
	};

	const successBody = () => {
		return userRequest?.userReqType === 'TARIFF_CHANGE'
			? [
					<BodyM>{translate('service.requests.accept.success.under.process')}</BodyM>,
					<BodyM>
						{translate(
							'service.requests.accept.subject',
							userRequest?.requestorMsisdn || '',
							userRequest?.reqParam.productsToAdd[0]?.name || '',
							userRequest?.reqParam.productsToAdd[1]?.name || '',
							userRequest?.amount || ''
						)}
					</BodyM>,
			  ]
			: [<BodyM>{translate('service.requests.accept.success.under.process')}</BodyM>];
	};

	return (
		<>
			<AcceptDialog
				isOpen={isOpen && requestStateDialog === RequestState.SHOW}
				clearAndTurnOffDialog={clearAndTurnOffDialog}
				icon={<InfoOutlined style={{ color: primaryColor[0], fontSize: '32px' }} />}
				header={<HeadingLBold>{translate('service.requests.accept.header')}</HeadingLBold>}
				body={showBody()}
				buttons={
					<>
						<Button color="secondary-light" onClick={clearAndTurnOffDialog}>
							{translate('back')}
						</Button>
						<Button color="primary-light" onClick={handleAccept}>
							{translate('service.requests.accept')}
						</Button>
					</>
				}
			/>
			<AcceptDialog
				isOpen={isOpen && requestStateDialog === RequestState.LOADING}
				clearAndTurnOffDialog={clearAndTurnOffDialog}
				icon={<InfoOutlined style={{ color: primaryColor[0], fontSize: '32px' }} />}
				header={<HeadingLBold>{translate('loading')}</HeadingLBold>}
				body={[<CustomCircularProgress loading={true} text={translate('loading')} />]}
				buttons={<></>}
			/>
			<AcceptDialog
				isOpen={isOpen && requestStateDialog === RequestState.SUCCESS}
				clearAndTurnOffDialog={clearAndTurnOffDialog}
				icon={<CheckCircleOutlined style={{ color: successColor[0], fontSize: '64px' }} />}
				header={<HeadingLBold>{translate('success')}</HeadingLBold>}
				body={successBody()}
				buttons={
					<Button color="primary-light" onClick={clearAndTurnOffDialog}>
						{translate('okey')}
					</Button>
				}
			/>
			<AcceptDialog
				isOpen={isOpen && requestStateDialog === RequestState.FAILURE}
				clearAndTurnOffDialog={clearAndTurnOffDialog}
				icon={<HighlightOffOutlined style={{ color: dangerColor[0], fontSize: '64px' }} />}
				header={<HeadingLBold>{translate('service.requests.accept.failure.header')}</HeadingLBold>}
				body={[<BodyM>{translate('service.requests.accept.failure.try.again')}</BodyM>]}
				buttons={
					<Button color="primary-light" onClick={clearAndTurnOffDialog}>
						{translate('okey')}
					</Button>
				}
			/>
		</>
	);
};

const AcceptDialog = ({
	isOpen,
	clearAndTurnOffDialog,
	icon,
	header,
	body,
	buttons,
}: {
	isOpen: boolean;
	clearAndTurnOffDialog: () => void;
	icon: ReactNode;
	header: ReactNode;
	body: Array<ReactNode>;
	buttons: ReactNode;
}) => {
	const singleLineStyle = { display: 'flex', justifyContent: 'center', marginBottom: '12px' };
	return (
		<CustomDialog
			isOpen={isOpen}
			hideDialog={clearAndTurnOffDialog}
			width={388}
			dialogBody={
				<div style={{ overflowX: 'hidden', margin: 48, textAlign: 'center' }}>
					<div style={singleLineStyle}>{icon}</div>
					<div style={singleLineStyle}>{header}</div>
					{body.map((bodyLine) => (
						<div style={singleLineStyle}>{bodyLine}</div>
					))}
					<div style={singleLineStyle}>{buttons}</div>
				</div>
			}
		/>
	);
};

export default AcceptRequestDialog;
